/**
 * TruePrice V1 - User Profile Types
 *
 * Defines the user profile schema for Firestore storage.
 * Source: SSOT, Addendum E (highest_sale addition)
 */

import { CareerStageKey } from './career';
import { TierKey } from './tier';
import { MediumKey } from './medium';

/**
 * Precision type for numeric inputs
 * "exact" = user has calculated/tracked the value
 * "estimated" = user is guessing/approximating
 */
export type Precision = 'exact' | 'estimated';

/**
 * Studio inputs interface for the Studio Overview/Edit pages
 */
export interface StudioInputs {
  monthlyBurnUsd: number | null;
  monthlyBurnPrecision: Precision;
  capacityHoursPerMonth: number | null;
  capacityPrecision: Precision;
  careerStage: CareerStageKey;
  highestSaleUsd: number | null;
  highestSalePrecision: Precision;
  pdfBrandName: string | null;
  location: string | null;
}

/**
 * Read studio inputs from profile, applying defaults for missing precision fields
 */
export function readStudioInputs(profile: UserProfile | null): StudioInputs {
  if (!profile) {
    return {
      monthlyBurnUsd: null,
      monthlyBurnPrecision: 'exact',
      capacityHoursPerMonth: null,
      capacityPrecision: 'exact',
      careerStage: 'emerging',
      highestSaleUsd: null,
      highestSalePrecision: 'exact',
      pdfBrandName: null,
      location: null,
    };
  }

  return {
    monthlyBurnUsd: profile.monthlyBurn ? profile.monthlyBurn / 100 : null,
    monthlyBurnPrecision: profile.monthlyBurnPrecision || 'exact',
    capacityHoursPerMonth: profile.monthlyCapacity || null,
    capacityPrecision: profile.capacityPrecision || 'exact',
    careerStage: profile.careerStage || 'emerging',
    highestSaleUsd: profile.highestSale ? profile.highestSale / 100 : null,
    highestSalePrecision: profile.highestSalePrecision || 'exact',
    pdfBrandName: profile.pdfBrandName || null,
    location: profile.location || null,
  };
}

/**
 * User profile stored in Firestore at users/{uid}
 */
export interface UserProfile {
  // Identity
  id: string;                              // Firebase UID
  email: string;
  displayName: string | null;
  photoURL?: string | null;                // Avatar URL (Firebase Storage)

  // Financial inputs
  monthlyBurn: number;                     // Cents. Total monthly expenses.
  monthlyBurnConfidence: 'calculated' | 'estimate';  // Legacy - kept for backwards compat
  monthlyBurnPrecision?: Precision;        // New precision field
  monthlyCapacity: number;                 // Hours. Realistic billable hours.
  capacityPrecision?: Precision;           // New precision field

  // Career
  careerStage: CareerStageKey;

  // Location
  location: string | null;           // Geographic location (required for onboarding)
  locationZip?: string;              // 5-digit zip code

  // Sales history (Addendum E)
  highestSale: number | null;              // Cents. Null = no recorded sales.
  highestSalePrecision?: Precision;        // New precision field
  highestSaleDate: string | null;          // ISO date string
  highestSaleMedium: MediumKey | null;
  highestSaleSource?: 'verdict' | 'manual'; // How the value was set
  highestSaleBuyer?: string | null;        // Optional buyer name

  // PDF Branding
  pdfBrandName?: string | null;            // Brand name shown on PDFs
  
  // Preferences
  defaultTier: TierKey;                    // User's default fidelity tier
  defaultMedium: MediumKey;                // User's default medium
  currency: string;                        // ISO 4217 code (e.g., "USD")
  
  // Subscription (Sprint 7)
  subscriptionStatus: SubscriptionStatus;
  subscriptionExpiresAt: string | null;    // ISO date string
  trialStartedAt: string | null;           // ISO date string
  
  // Bank connection (Plaid Phase 1)
  bankConnected?: boolean;
  burnSource?: 'plaid' | 'manual';

  // Reveal screen (Plaid Phase 2)
  revealSeen?: boolean;

  // Plaid connection ceremony (shown once after first bank connect)
  hasSeenConnectionCeremony?: boolean;

  // Phase 5 — unreviewed transaction counts (server-written)
  unreviewedSpendCount?: number;
  unreviewedEarnedCount?: number;

  // Burn auto-update (Plaid Phase 4)
  burnCalculatedAt?: string;        // ISO date, last auto-calculation
  monthlySuppliesAvg?: number;      // Cents, 3-month rolling avg of ART_SUPPLY

  // Earned unification (Phase 3)
  earnedThisMonthCents?: number;    // Three-pool earned total for current month
  earnedBreakdown?: {
    pool1: number;                  // Linked txn+verdict (soldPrice - materials)
    pool2: number;                  // Reported verdict-only (sold but no linked txn)
    pool3: number;                  // Detected txn-only (ART_INCOME, no linked verdict)
  };
  earnedLastUpdated?: string;       // ISO date string
  earnedMonth?: string;             // "YYYY-MM" key for current computation

  // Dashboard insights (Sprint 5A)
  dismissedInsights?: string[];

  // Feedback (Sprint 5B)
  lastFeedbackAt?: number | null;

  // Milestones (Sprint 5F)
  milestones?: {
    firstCalculation?: boolean;
    firstSale?: boolean;
    premiumActivated?: boolean;
  };

  // Notification preferences (Sprint 3C)
  notificationPrefs?: {
    floorAlerts?: boolean;
    weeklyDigest?: boolean;
    trialReminders?: boolean;
    pricingInsights?: boolean;
  };

  // Metadata
  createdAt: string;                       // ISO date string
  updatedAt: string;                       // ISO date string
  onboardingCompleted: boolean;
}

export type SubscriptionStatus = 'free' | 'trial' | 'premium' | 'expired';

/**
 * Default profile values for new users
 * Source: Addendum B Section 7.1 (Soft Defaults)
 */
export const DEFAULT_PROFILE: Omit<UserProfile, 'id' | 'email' | 'createdAt' | 'updatedAt'> = {
  displayName: null,
  monthlyBurn: 300000,        // $3,000 in cents (matches STUDIO_DEFAULTS.monthlyBurn)
  monthlyBurnConfidence: 'estimate',
  monthlyBurnPrecision: 'exact',
  monthlyCapacity: 130,       // hours
  capacityPrecision: 'exact',
  careerStage: 'emerging',
  location: null,
  highestSale: null,
  highestSalePrecision: 'exact',
  highestSaleDate: null,
  highestSaleMedium: null,
  pdfBrandName: null,
  defaultTier: 'L3',
  defaultMedium: 'oil',
  currency: 'USD',
  subscriptionStatus: 'free',
  subscriptionExpiresAt: null,
  trialStartedAt: null,
  onboardingCompleted: false,
};

/**
 * Profile update payload (partial updates)
 */
export type ProfileUpdate = Partial<Omit<UserProfile, 'id' | 'email' | 'createdAt'>>;

/**
 * Check if user has completed onboarding
 * Required fields: careerStage (set), monthlyBurn (> 0)
 */
export function isOnboardingComplete(profile: UserProfile | null): boolean {
  if (!profile) return false;
  return profile.onboardingCompleted === true;
}

/**
 * Create a new profile with defaults
 */
export function createProfile(uid: string, email: string): UserProfile {
  const now = new Date().toISOString();
  return {
    ...DEFAULT_PROFILE,
    id: uid,
    email,
    createdAt: now,
    updatedAt: now,
  };
}
